// src/sectors/pilates/members/pages/PilatesMembersPage.jsx
import React from 'react';
import MDBox from 'components/MDBox';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import { MembersToolbar } from '../components/MembersToolbar';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { MemberEditModal } from '../modal/MemberEditModal/MemberEditModal';

import DataTable from 'examples/Tables/DataTable';

import { membersApi } from '../api/membersApi';
import { MembersAccordionList } from '../components/MembersAccordionList';
import { MemberCreateModal } from '../modal/MemberCreateModal/MemberCreateModal';

// ✅ yeni header bar (tablo üstünde arama + filtre + yeni)
import { MembersTableHeaderBar } from '../components/MembersTableHeaderBar';

// ✅ table builder
import membersTableData from '../data/membersTableData';
import { MemberDetailModal } from '../modal/MemberDetailModal/MemberDetailModal';
import { SmsSendModal } from '../components/MemberActionsMenu/SmsSendModal';
import { MembersActionsMenu } from '../components/MemberActionsMenu/MembersActionsMenu';
import { PaymentReceiveModal } from '../components/paymentReceive/PaymentReceiveModal';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';
import { Alert, Snackbar } from '@mui/material';
import { smsApi } from 'sectors/pilates/sms/api/smsApi';

// Staff’teki StateCard aynı pattern
function StateCard({ icon, title, subtitle, actionLabel, onAction }) {
  const theme = useMuiTheme();

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: '22px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[6],
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})`,
        textAlign: 'center',
      }}
    >
      <MDBox
        sx={{
          width: 64,
          height: 64,
          borderRadius: '22px',
          display: 'grid',
          placeItems: 'center',
          mx: 'auto',
          background: theme.palette.action.hover,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[2],
        }}
      >
        <Icon sx={{ fontSize: 34, opacity: 0.85 }}>{icon}</Icon>
      </MDBox>

      <MDTypography variant="h6" fontWeight="bold" sx={{ mt: 1.4 }}>
        {title}
      </MDTypography>
      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{ color: theme.palette.text.secondary, opacity: 1 }}
      >
        {subtitle}
      </MDTypography>

      {actionLabel ? (
        <MDBox mt={2}>
          <MDButton
            variant="gradient"
            color="info"
            sx={{ borderRadius: '14px' }}
            onClick={onAction}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>person_add</Icon>
            {actionLabel}
          </MDButton>
        </MDBox>
      ) : null}
    </Card>
  );
}

export default function PilatesMembersPage() {
  const theme = useMuiTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [paymentMember, setPaymentMember] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  const [err, setErr] = React.useState(null);

  // search (header bar)
  const [q, setQ] = React.useState('');

  // ✅ filters (header bar popover)
  // "ALL" | trainerId
  const [trainerId, setTrainerId] = React.useState('ALL');
  // "" => all
  const [planName, setPlanName] = React.useState('');
  // ALL | DEBT | NO_DEBT
  const [paymentStatus, setPaymentStatus] = React.useState('ALL');
  // ALL | ACTIVE | PASSIVE | FROZEN
  const [status, setStatus] = React.useState('ALL');

  // menu
  const [menuEl, setMenuEl] = React.useState(null);
  const [menuMember, setMenuMember] = React.useState(null);

  // modals
  const [createOpen, setCreateOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  // ✅ "Mesaj çok yakında" modal
  const [soonOpen, setSoonOpen] = React.useState(false);
  const openMessageSoon = () => {
    // menuMember zaten state’de
    setSoonOpen(true);
  };
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const openPayment = async (m = menuMember) => {
    const id = resolveMemberId(m);

    if (!id) {
      console.warn('❌ openPayment: memberId bulunamadı', m);
      return;
    }

    try {
      // ✅ gerçek DTO
      const full = await membersApi.detail(id);
      setPaymentMember(full ?? { ...m, id });
      setPaymentOpen(true);
    } catch (e) {
      console.error('❌ membersApi.detail failed:', e);
      // fallback: hiç yoktan id ile aç
      setPaymentMember({ ...m, id });
      setPaymentOpen(true);
    }
  };

  const resolveMemberId = (m) =>
    m?.id ?? m?._id ?? m?.memberId ?? m?.original?.id ?? m?.original?._id ?? m?.original?.memberId;

  // ✅ "Ödeme al" modal

  const fetchList = React.useCallback(async () => {
    try {
      setErr(null);
      setLoading(true);

      // ✅ backend list dönüyor (Page değil)
      // şimdilik tüm listeyi çekiyoruz; filtreleri client-side uyguluyoruz
      const res = await membersApi.list();
      const arr = Array.isArray(res) ? res : res?.items ?? [];
      setItems(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.error('membersApi.list failed:', e);
      setErr(e?.message || 'Üyeler yüklenemedi.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);
  const [sortBy, setSortBy] = React.useState('CREATED_DESC');
  // ✅ client-side filtreleme
  const filtered = React.useMemo(() => {
    const qq = q.trim().toLowerCase();

    const debtOf = (m) => {
      const v =
        typeof m?.debtTry === 'number'
          ? m.debtTry
          : typeof m?.balanceCents === 'number'
          ? Math.round(m.balanceCents / 100)
          : 0;
      return Math.max(0, v);
    };

    const nameOf = (m) =>
      String(m?.fullName || `${m?.name || ''} ${m?.surname || ''}`.trim() || '');

    // 1) FILTER
    let list = (items || []).filter((m) => {
      if (status !== 'ALL') {
        const ms = String(m?.membershipStatus || '').toUpperCase();
        if (status === 'ACTIVE' && ms !== 'ACTIVE') return false;
        if (status === 'PASSIVE' && ms !== 'PASSIVE') return false;
        if (status === 'FROZEN' && ms !== 'FROZEN') return false;
      }

      if (trainerId !== 'ALL') {
        if (String(m?.assignedTrainerId ?? '') !== String(trainerId)) return false;
      }

      if (planName) {
        const pn = m?.activePlanName || m?.planName || '';
        if (String(pn) !== String(planName)) return false;
      }

      const debt = debtOf(m);
      if (paymentStatus === 'DEBT' && !(debt > 0)) return false;
      if (paymentStatus === 'NO_DEBT' && !(debt <= 0)) return false;

      if (!qq) return true;

      const fullName = nameOf(m).toLowerCase();
      const phone = String(m?.phone || '').toLowerCase();
      const pnLow = String(m?.activePlanName || m?.planName || '').toLowerCase();
      const trainerLabel = m?.assignedTrainerName || m?.trainerName || '';
      const tnLow = String(trainerLabel).toLowerCase();

      return (
        fullName.includes(qq) || phone.includes(qq) || pnLow.includes(qq) || tnLow.includes(qq)
      );
    });

    // 2) SORT
    list = [...list].sort((a, b) => {
      if (sortBy === 'DEBT_DESC') return debtOf(b) - debtOf(a);
      if (sortBy === 'DEBT_ASC') return debtOf(a) - debtOf(b);

      if (sortBy === 'NAME_ASC') return nameOf(a).localeCompare(nameOf(b), 'tr');

      if (sortBy === 'CREATED_DESC') {
        const ta = new Date(a?.createdAt || 0).getTime();
        const tb = new Date(b?.createdAt || 0).getTime();
        return tb - ta;
      }

      return 0;
    });

    return list;
  }, [items, q, trainerId, planName, paymentStatus, status, sortBy]);

  // KPI’lar (StateCard subtitle vs)
  const totalCount = items.length;
  const activeCount = items.filter(
    (x) => String(x?.membershipStatus).toUpperCase() === 'ACTIVE'
  ).length;
  const passiveCount = items.filter(
    (x) => String(x?.membershipStatus).toUpperCase() === 'PASSIVE'
  ).length;

  const debtCount = React.useMemo(() => {
    return (items || []).filter((m) => {
      const debtTry =
        typeof m?.debtTry === 'number'
          ? m.debtTry
          : typeof m?.balanceCents === 'number'
          ? Math.round(m.balanceCents / 100)
          : 0;
      return debtTry > 0;
    }).length;
  }, [items]);

  // menu handlers
  const openMenu = Boolean(menuEl);

  const openDetail = React.useCallback((member) => {
    setDetailMember(member);
    setDetailOpen(true);
  }, []);

  const handleOpenMenu = React.useCallback((e, member) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const anchor =
      e?.currentTarget?.closest?.('button') ||
      e?.currentTarget ||
      e?.target?.closest?.('button') ||
      e?.target;

    setMenuEl(anchor);
    setMenuMember(member);
  }, []);

  const closeMenu = () => {
    setMenuEl(null);
    setMenuMember(null);
  };

  const openCreate = () => setCreateOpen(true);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailMember, setDetailMember] = React.useState(null);
  const [smsOpen, setSmsOpen] = React.useState(false);
  const [smsMember, setSmsMember] = React.useState(null);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [confirmErr, setConfirmErr] = React.useState(null);
  const [confirmMember, setConfirmMember] = React.useState(null);
  const [confirmNextActive, setConfirmNextActive] = React.useState(true);
  const askToggleActive = (member) => {
    closeMenu();

    const id = resolveMemberId(member);
    if (!id) return;

    const isInactive = member?.active === false;
    const nextActive = isInactive; // inactive ise aktifleştir, değilse pasife al

    setConfirmErr(null);
    setConfirmMember({ ...member, id });
    setConfirmNextActive(nextActive);
    setConfirmOpen(true);
  };
  const doToggleActive = async () => {
    if (!confirmMember?.id) return;

    setConfirmBusy(true);
    setConfirmErr(null);

    try {
      // ✅ backend endpoint: PATCH /members/{id}/active { active: true/false }
      const res = await membersApi.setActive(confirmMember.id, confirmNextActive);

      // ✅ Listeyi anında güncelle (full refresh yerine)
      setItems((prev) =>
        (prev || []).map((m) => {
          const mid = resolveMemberId(m);
          if (String(mid) !== String(confirmMember.id)) return m;

          // backend response dönerse onu merge et, yoksa local değiştir
          const next = res && typeof res === 'object' ? { ...m, ...res } : { ...m };
          next.active = !!confirmNextActive;

          // eğer backend membershipStatus da güncelliyorsa zaten merge olur,
          // güncellemiyorsa localde de tutarlı yapalım:
          if (!confirmNextActive) next.membershipStatus = 'PASSIVE';
          else if (String(next.membershipStatus || '').toUpperCase() === 'PASSIVE')
            next.membershipStatus = 'ACTIVE';

          return next;
        })
      );

      setConfirmOpen(false);
      setConfirmMember(null);
    } catch (e) {
      console.error('❌ setActive failed:', e);
      setConfirmErr(e?.message || 'İşlem başarısız.');
    } finally {
      setConfirmBusy(false);
    }
  };
  const [toast, setToast] = React.useState({ open: false, severity: 'success', msg: '' });
  const showToast = (severity, msg) => setToast({ open: true, severity, msg });
  const askMessage = async (member) => {
    closeMenu();
    const id = resolveMemberId(member);
    if (!id) return;

    try {
      const full = await membersApi.detail(id);
      const resolved = full ?? { ...member, id };
      console.log('SMS member resolved:', resolved); // ✅ bir kez bak
      setSmsMember(resolved);
    } catch (e) {
      const resolved = { ...member, id };
      console.log('SMS member fallback:', resolved);
      setSmsMember(resolved);
    }

    setSmsOpen(true);
  };

  // table builder
  const table = React.useMemo(
    () =>
      membersTableData({
        items: filtered,
        onOpenMenu: handleOpenMenu,
        onOpenDetail: openDetail,
      }),
    [filtered, handleOpenMenu, openDetail]
  );
  const resolveMemberName = (m) => {
    if (!m) return '';
    const fn = (m.fullName ?? '').toString().trim();
    if (fn) return fn;

    const name = (m.name ?? '').toString().trim();
    const surname = (m.surname ?? '').toString().trim();
    const joined = `${name} ${surname}`.trim();
    if (joined) return joined;

    const alt = (m.memberName ?? '').toString().trim();
    return alt;
  };
  // options (filters)
  const trainerOptions = React.useMemo(() => {
    // member dto: assignedTrainerId + trainerName geliyorsa
    const map = new Map();
    (items || []).forEach((m) => {
      if (m?.assignedTrainerId != null) {
        const label = m?.assignedTrainerName || m?.trainerName || `#${m.assignedTrainerId}`;
        map.set(String(m.assignedTrainerId), label);
      }
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [items]);
  // sort: NONE | DEBT_DESC | DEBT_ASC | NAME_ASC | CREATED_DESC
  const planOptions = React.useMemo(() => {
    const set = new Set();
    (items || []).forEach((m) => {
      const pn = m?.activePlanName || m?.planName;
      if (pn) set.add(String(pn));
    });
    return Array.from(set);
  }, [items]);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editMember, setEditMember] = React.useState(null);
  const openEdit = async (m = menuMember) => {
    const id = resolveMemberId(m);
    if (!id) return;

    try {
      const full = await membersApi.detail(id); // ✅ içi dolu gelsin
      setEditMember(full ?? { ...m, id });
    } catch (e) {
      console.error('membersApi.detail failed (edit):', e);
      setEditMember({ ...m, id });
    } finally {
      setEditOpen(true);
    }
  };
  const handleCreateMember = async (dto) => {
    await membersApi.create(dto);
    await fetchList(); // veya loadMembers()
  };
  return (
    <MDBox sx={{ width: '100%' }}>
      {/* ✅ EN ÜST KPI / TOOLBAR */}
      <MDBox sx={{ mt: 2 }}>
        <MembersToolbar
          totalCount={totalCount}
          activeCount={activeCount}
          passiveCount={passiveCount}
          debtCount={debtCount}
          onCreate={openCreate}
        />
      </MDBox>
      {/* ✅ içerik */}
      <MDBox sx={{ mt: 2 }}>
        {loading ? (
          <StateCard
            icon="hourglass_top"
            title="Yükleniyor…"
            subtitle="Üye listesi hazırlanıyor."
          />
        ) : err ? (
          <StateCard
            icon="error_outline"
            title="Bir şey ters gitti"
            subtitle={err}
            actionLabel="Tekrar dene"
            onAction={fetchList}
          />
        ) : (
          <Card
            sx={{
              borderRadius: isMobile ? 0 : '22px',
              border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
              boxShadow: isMobile ? 'none' : theme.shadows[8],
              overflow: 'hidden',
              // mobilde ekranı doldursun
              mx: isMobile ? -2 : 0, // sayfa padding’ini sıfırlar (MDBox mt:2’nin içine gömülü)
              width: isMobile ? 'calc(100% + 32px)' : 'auto',
            }}
          >
            {/* ✅ HEADER BAR HER ZAMAN */}
            <MDBox
              sx={{
                position: { xs: 'sticky', md: 'static' },
                top: { xs: 'env(safe-area-inset-top, 0px)', md: 0 },
                zIndex: 20,
                background: theme.palette.background.paper,
                // mobilde header daha “bar” gibi dursun
                boxShadow: { xs: '0 10px 18px rgba(0,0,0,.06)', md: 'none' },
              }}
            >
              <MembersTableHeaderBar
                title="Üye Listesi"
                q={q}
                onQ={setQ}
                onCreate={openCreate}
                onRefresh={fetchList}
                sortBy={sortBy}
                onSortBy={setSortBy}
                count={filtered.length}
                filters={{ trainerId, planName, paymentStatus, status }}
                onChangeFilters={(next) => {
                  setTrainerId(next.trainerId);
                  setPlanName(next.planName);
                  setPaymentStatus(next.paymentStatus);
                  setStatus(next.status);
                }}
                trainerOptions={trainerOptions}
                planOptions={planOptions}
                // ✅ mobil optimizasyon
                isMobile={isMobile}
                onOpenFilters={() => setFiltersOpen(true)}
              />

              <Divider />
            </MDBox>

            {/* ✅ BODY */}
            <MDBox
              sx={{
                p: { xs: 1, md: 2 },
                pt: { xs: 1, md: 2 },
                // mobilde listeye nefes
                pb: { xs: 9, md: 2 }, // FAB için altta boşluk
              }}
            >
              {filtered.length === 0 ? (
                <StateCard
                  icon="search_off"
                  title="Kayıt bulunamadı"
                  subtitle={`Filtreyi kapat veya aramayı temizle. (Toplam: ${totalCount} • Aktif: ${activeCount} • Borçlu: ${debtCount})`}
                  actionLabel="Yeni Üye"
                  onAction={openCreate}
                />
              ) : isDesktop ? (
                <DataTable
                  table={table}
                  isSorted={true}
                  entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }}
                  showTotalEntries
                  noEndBorder
                />
              ) : (
                <MembersAccordionList
                  items={filtered}
                  onOpenMenu={handleOpenMenu}
                  onDetail={openDetail}
                />
              )}
            </MDBox>
          </Card>
        )}
      </MDBox>
      {/* actions menu */}
      <MembersActionsMenu
        anchorEl={menuEl}
        open={openMenu}
        onClose={closeMenu}
        member={menuMember}
        onDetail={(m) => {
          closeMenu();
          openDetail(m);
        }}
        onToggleActive={askToggleActive}
        onMessageSoon={(m) => {
          askMessage(m ?? menuMember);
        }}
        onOpenPayment={async (m) => {
          closeMenu();
          await openPayment(m);
        }}
        onEdit={async (m) => {
          closeMenu();
          await openEdit(m);
        }}
      />

      <PaymentReceiveModal
        open={paymentOpen}
        member={paymentMember}
        onClose={() => {
          setPaymentOpen(false);
          setPaymentMember(null);
        }}
        onSuccess={async () => {
          try {
            showToast('success', 'Ödeme başarıyla alındı.');

            setPaymentOpen(false);
            setPaymentMember(null);

            await fetchList();
          } catch (e) {
            console.error(e);
            showToast('error', 'Liste yenilenirken bir sorun oluştu.');
          }
        }}
      />
      <MemberCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreateMember}
      />
      <MemberDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        member={detailMember}
      />
      <SmsSendModal
        open={smsOpen}
        onClose={() => {
          setSmsOpen(false);
          setSmsMember(null);
        }}
        member={{
          id: resolveMemberId(smsMember),
          fullName: resolveMemberName(smsMember),
          phone: smsMember?.phone,
        }}
        companyName={null}
        defaultMessage=""
        onSend={async ({ memberId, phone, message }) => {
          // ✅ backend OneShotSmsRequest ile birebir
          const payload = {
            memberId: memberId ?? null,
            memberName: resolveMemberName(smsMember) || null,
            message,
            phone, // normalizeTrPhone zaten modalda yapılıyor
          };

          const res = await smsApi.sendManual(payload);

          // SmsSendResult: { ok:false, errors:[...], ... }
          if (res?.ok === false) {
            const err =
              res?.error ||
              (Array.isArray(res?.errors) ? res.errors[0] : null) ||
              res?.message ||
              'SMS gönderilemedi.';

            showToast('error', err);
            throw new Error(err);
          }

          showToast('success', 'SMS gönderildi.');
        }}
      />
      <MemberEditModal
        open={editOpen}
        member={editMember}
        onClose={() => {
          setEditOpen(false);
          setEditMember(null);
        }}
        onSave={async ({ id, patch }) => {
          try {
            console.log('✅ MEMBER EDIT SAVE PAYLOAD:', { id, patch });

            await membersApi.update(id, patch);
            await fetchList();

            showToast('success', 'Üye bilgileri başarıyla güncellendi.');

            setEditOpen(false);
            setEditMember(null);
          } catch (e) {
            console.error('❌ MEMBER EDIT SAVE FAILED:', e);
            showToast('error', e?.message || 'Üye güncellenirken bir sorun oluştu.');
          }
        }}
      />
      {/* ✅ confirm active/passive */}
      <ConfirmDialog
        open={confirmOpen}
        tone={confirmNextActive ? 'success' : 'error'}
        title={confirmNextActive ? 'Üyeyi Aktifleştir' : 'Üyeyi Pasife Al'}
        subtitle={confirmMember?.fullName || 'Üye'}
        message={
          confirmErr
            ? `Hata: ${confirmErr}`
            : confirmNextActive
            ? 'Bu üye tekrar aktif görünecek. Devam edilsin mi?'
            : 'Bu üye pasife alınacak ve listelerde pasif olarak görünecek. Devam edilsin mi?'
        }
        confirmText={confirmNextActive ? 'Evet, Aktifleştir' : 'Evet, Pasife Al'}
        cancelText="Vazgeç"
        busy={confirmBusy}
        onCancel={() => {
          if (confirmBusy) return;
          setConfirmOpen(false);
          setConfirmMember(null);
          setConfirmErr(null);
        }}
        onConfirm={doToggleActive}
      />
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}
